import {
  AddReactionRounded as AddIcon,
  ClearRounded as ClearIcon,
  SentimentVerySatisfiedRounded as MoreIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import EmojiPickerReact from "emoji-picker-react";
import { useState } from "react";

const EMOJI_OPTIONS = [
  { emoji: "❤️", color: "#ffebee" },
  { emoji: "☘️", color: "#e8f5e9" },
  { emoji: "⭐", color: "#fff8e1" },
  { emoji: "🔥", color: "#fff3e0" },
  { emoji: "👀", color: "#e3f2fd" },
  { emoji: "✅", color: "#e0f2f1" },
  { emoji: "📌", color: "#fce4ec" },
  { emoji: "💼", color: "#f3e5f5" },
];

const EmojiButton = ({ emoji, color, isSelected, onClick }) => (
  <IconButton
    onClick={onClick}
    sx={{
      width: 40,
      height: 40,
      fontSize: "1.25rem",
      color: "unset",
      backgroundColor: isSelected ? color : "transparent",
      border: isSelected ? `2px solid ${color}` : "2px solid transparent",
      "&:hover": {
        backgroundColor: color,
      },
    }}
  >
    {emoji}
  </IconButton>
);

const EmojiPicker = ({
  selectedEmojis = [],
  onToggleEmoji,
  disabled = false,
  size = "small",
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showFullPicker, setShowFullPicker] = useState(false);

  const handleOpen = (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (!disabled) {
      setAnchorEl(event.currentTarget);
      setShowFullPicker(false);
    }
  };

  const handleClose = (event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    setAnchorEl(null);
    setShowFullPicker(false);
  };

  const handleToggle = (emoji, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    onToggleEmoji(emoji);
    setAnchorEl(null);
    setShowFullPicker(false);
  };

  const handleRemove = (event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    selectedEmojis.forEach((emoji) => onToggleEmoji(emoji));
    setAnchorEl(null);
    setShowFullPicker(false);
  };

  const handleFullPickerSelect = (emojiData) => {
    onToggleEmoji(emojiData.emoji);
    setAnchorEl(null);
    setShowFullPicker(false);
  };

  const open = Boolean(anchorEl);
  const fontSize = size === "large" ? "1.25rem" : "1rem";

  return (
    <>
      <Tooltip title={"Tag with emoji"}>
        <IconButton
          size={size}
          onClick={handleOpen}
          disabled={disabled}
          sx={{
            color: "unset",
            opacity: selectedEmojis.length > 0 ? 1 : 0.2,
            "&:hover": { opacity: 1 },
          }}
        >
          {selectedEmojis.length > 0 ? (
            <span style={{ fontSize, height: fontSize, lineHeight: fontSize }}>
              {selectedEmojis[0]}
            </span>
          ) : (
            <AddIcon fontSize={size === "large" ? "small" : "inherit"} />
          )}
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {!showFullPicker ? (
          <Stack p={1.5} alignItems={"center"}>
            <Typography
              variant={"caption"}
              color={"text.secondary"}
              sx={{ mb: 1, display: "block" }}
            >
              {"Tag this submission"}
            </Typography>
            <Stack
              direction={"row"}
              flexWrap={"wrap"}
              gap={0.5}
              maxWidth={190}
              justifyContent={"center"}
            >
              {EMOJI_OPTIONS.map(({ emoji, color }) => (
                <EmojiButton
                  key={emoji}
                  emoji={emoji}
                  color={color}
                  isSelected={selectedEmojis.includes(emoji)}
                  onClick={(e) => handleToggle(emoji, e)}
                />
              ))}
            </Stack>
            <Button
              size={"small"}
              fullWidth
              variant={"outlined"}
              startIcon={<MoreIcon />}
              onClick={(e) => {
                e.stopPropagation();
                setShowFullPicker(true);
              }}
              sx={{
                marginTop: "0.75rem",
                textTransform: "none",
                color: "text.primary",
                borderColor: "divider",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
            >
              {"Custom"}
            </Button>
            {selectedEmojis.length > 0 && (
              <Button
                size={"small"}
                fullWidth
                variant={"outlined"}
                startIcon={<ClearIcon />}
                onClick={handleRemove}
                sx={{
                  marginTop: "0.5rem",
                  textTransform: "none",
                  color: "error.main",
                  borderColor: "error.light",
                  "&:hover": {
                    borderColor: "error.main",
                    backgroundColor: "error.lighter",
                  },
                }}
              >
                {"Remove tag"}
              </Button>
            )}
          </Stack>
        ) : (
          <Box onClick={(e) => e.stopPropagation()}>
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{
                px: 1.5,
                py: 1,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Button
                size={"small"}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFullPicker(false);
                }}
                sx={{ textTransform: "none", color: "lightslategrey" }}
                variant={"text"}
                color={"secondary"}
              >
                {"← Back to quick picks"}
              </Button>
            </Stack>
            <EmojiPickerReact
              onEmojiClick={handleFullPickerSelect}
              width={350}
              height={400}
              searchPlaceholder={"Search emojis..."}
              previewConfig={{ showPreview: false }}
            />
          </Box>
        )}
      </Popover>
    </>
  );
};

export default EmojiPicker;
